import React, { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import {
  ExportOutlined,
  LoadingOutlined,
  MoreOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import { Spin } from 'antd';
import styled from 'styled-components';

import {
  getRingCentralCommunication,
  matchRingCentralCommunication,
  unassignRingCentralCommunication,
} from 'apis/RingCentralAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import Dropdown from 'components/lib/Dropdown';
import GradientDivider from 'components/lib/GradientDivider';
import Modal from 'components/lib/Modal';

import { RINGCENTRAL_RECORDING_MEDIA_GROUP_ORDER, RINGCENTRAL_TRANSCRIPT_MEDIA_GROUP_ORDER } from './constants';
import {
  Badge,
  BadgeGroup,
  ContactValue,
  EmptyValue,
  formatDate,
  formatDuration,
  formatLabel,
  PrimaryValue,
  RingCentralRecordTag,
  SecondaryValue,
  ValueContainer,
} from './RingCentralRecordsDisplay';
import {
  RingCentralCommunicationMediaType,
  RingCentralCommunicationType,
  RingCentralTranscriptSegmentType,
} from './types';

const DetailContainer = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 18px;
  min-height: 0;
  padding: 20px 30px;
  background: var(--background-gray);
`;

const RecordMediaModal = styled(Modal)`
  &&& {
    max-width: calc(100vw - 48px);

    .ant-modal-content {
      display: flex;
      flex-direction: column;
      height: 70vh;
    }

    .ant-modal-body {
      display: flex;
      flex: 1;
      min-height: 0;
    }
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, max-content));
  gap: 8px 18px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const DetailLabel = styled.span`
  font-family: var(--font-bold);
  font-size: 11px;
  color: var(--text-secondary);
  text-transform: uppercase;
`;

const MediaList = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 10px;
  min-height: 0;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-gutter: stable;
`;

const LinkedMediaGroup = styled.div<{ $isLinked: boolean }>`
  display: flex;
  flex-direction: column;

  ${({ $isLinked }) =>
    $isLinked &&
    `
      > .ringcentral-media-item + .ringcentral-media-item {
        border-top: 0;
      }
    `}
`;

const ModalRecordSummary = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 0.7fr 0.8fr 1.1fr 1.1fr 1.3fr 0.9fr 1.4fr 0.8fr 0.5fr;
  gap: 12px;
  justify-items: start;
  text-align: left;

  @media (max-width: 1150px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const ModalSummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 0;
  text-align: left;
`;

const ActionSummaryItem = styled(ModalSummaryItem)`
  padding-top: 17px;
`;

const ActionsIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 18px;

  &:hover:not(:disabled) {
    color: var(--text-primary);
  }

  &:disabled {
    cursor: default;
  }
`;

const MediaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid var(--line-gray);
  background: var(--white);
`;

const MediaHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
`;

const TextBlock = styled.pre`
  max-height: 260px;
  margin: 0;
  padding: 10px;
  overflow: auto;
  border: 1px solid var(--line-gray);
  background: var(--white);
  color: var(--text-primary);
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
`;

const SpeakerLabel = styled.span`
  color: var(--text-primary);
  font-family: var(--font-bold);
`;

const ExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 26px;
  padding: 0 8px;
  color: var(--deep-blue);
  font-size: 12px;
  word-break: break-word;

  &:hover {
    color: var(--button-blue);
    text-decoration: underline;
  }
`;

const FileActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const AudioControlButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 26px;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--deep-blue);
  cursor: pointer;
  font-size: 19px;

  &:hover {
    color: var(--button-blue);
  }
`;

const HiddenAudio = styled.audio`
  display: none;
`;

const AudioTime = styled.span`
  color: var(--text-secondary);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px;
`;

const getMediaGroups = (mediaItems: RingCentralCommunicationMediaType[]) => {
  const mediaGroups = new Map<string, RingCentralCommunicationMediaType[]>();

  mediaItems.forEach((media) => {
    const groupKey = media.media_group_id || `media:${media.id}`;
    const mediaGroup = mediaGroups.get(groupKey);
    if (mediaGroup) {
      mediaGroup.push(media);
      return;
    }

    mediaGroups.set(groupKey, [media]);
  });

  return Array.from(mediaGroups.values()).map((mediaGroup) => {
    const sortedMediaGroup = [...mediaGroup];
    sortedMediaGroup.sort(
      (firstMedia, secondMedia) => getMediaGroupOrder(firstMedia) - getMediaGroupOrder(secondMedia)
    );
    return sortedMediaGroup;
  });
};

const getMediaGroupOrder = (media: RingCentralCommunicationMediaType) => {
  if (media.media_type === 'call_recording' || media.media_type === 'audio_recording') {
    return RINGCENTRAL_RECORDING_MEDIA_GROUP_ORDER;
  }

  return RINGCENTRAL_TRANSCRIPT_MEDIA_GROUP_ORDER;
};

const getTranscriptSpeakers = (segments: RingCentralTranscriptSegmentType[]) => {
  const speakersById = new Map<string, { id: string; label: string }>();

  segments.forEach((segment) => {
    if (segment.speaker_id && segment.speaker_label && !speakersById.has(segment.speaker_id)) {
      speakersById.set(segment.speaker_id, {
        id: segment.speaker_id,
        label: segment.speaker_label,
      });
    }
  });

  return Array.from(speakersById.values());
};

const RingCentralRecordMediaItem = ({ media }: { media: RingCentralCommunicationMediaType }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [totalSeconds, setTotalSeconds] = useState(media.duration_seconds ?? null);
  const canPlayAudio = Boolean(
    media.file_url &&
    (media.content_type?.toLowerCase().startsWith('audio/') ||
      media.media_type === 'audio_recording' ||
      media.media_type === 'call_recording')
  );
  const transcriptSpeakers = getTranscriptSpeakers(media.transcript_segments ?? []);
  const toggleAudioPlayback = async () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (audio.paused) {
      if (audio.ended) {
        audio.currentTime = 0;
        setElapsedSeconds(0);
      }

      try {
        await audio.play();
      } catch (error) {
        console.error('Failed to play RingCentral recording.', error);
      }
      return;
    }

    audio.pause();
  };

  const formatAudioTime = (seconds: number | null) => {
    if (seconds === null || !Number.isFinite(seconds)) {
      return '--:--';
    }

    const totalWholeSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalWholeSeconds / 60);
    const remainingSeconds = totalWholeSeconds % 60;

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <MediaItem className="ringcentral-media-item">
      <MediaHeader>
        <PrimaryValue>{formatLabel(media.media_type)}</PrimaryValue>
      </MediaHeader>
      <DetailGrid>
        <DetailItem>
          <DetailLabel>External ID</DetailLabel>
          <SecondaryValue>{media.external_id}</SecondaryValue>
        </DetailItem>
        {media.content_type && (
          <DetailItem>
            <DetailLabel>Format</DetailLabel>
            <SecondaryValue>{media.content_type}</SecondaryValue>
          </DetailItem>
        )}
        {formatDuration(media.duration_seconds) && (
          <DetailItem>
            <DetailLabel>Duration</DetailLabel>
            <SecondaryValue>{formatDuration(media.duration_seconds)}</SecondaryValue>
          </DetailItem>
        )}
        {media.file_name && (
          <DetailItem>
            <DetailLabel>File Name</DetailLabel>
            <SecondaryValue>{media.file_name}</SecondaryValue>
          </DetailItem>
        )}
        <DetailItem>
          <DetailLabel>File</DetailLabel>
          <SecondaryValue>
            {media.file_url ? (
              <FileActions>
                <ExternalLink href={media.file_url} target="_blank" rel="noreferrer">
                  Open file
                  <ExportOutlined />
                </ExternalLink>
                {canPlayAudio && (
                  <AudioControlButton
                    aria-label={isPlaying ? 'Pause recording' : 'Play recording'}
                    onClick={toggleAudioPlayback}
                    title={isPlaying ? 'Pause recording' : 'Play recording'}
                    type="button"
                  >
                    {isPlaying ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
                  </AudioControlButton>
                )}
                {canPlayAudio && (
                  <AudioTime>{`${formatAudioTime(elapsedSeconds)} / ${formatAudioTime(totalSeconds)}`}</AudioTime>
                )}
                {canPlayAudio && (
                  <HiddenAudio
                    onEnded={() => {
                      setElapsedSeconds(totalSeconds ?? 0);
                      setIsPlaying(false);
                    }}
                    onLoadedMetadata={(event) => setTotalSeconds(event.currentTarget.duration)}
                    onPause={() => setIsPlaying(false)}
                    onPlay={() => setIsPlaying(true)}
                    onTimeUpdate={(event) => setElapsedSeconds(event.currentTarget.currentTime)}
                    preload="metadata"
                    ref={audioRef}
                    src={media.file_url}
                  />
                )}
              </FileActions>
            ) : (
              <EmptyValue>None</EmptyValue>
            )}
          </SecondaryValue>
        </DetailItem>
        {transcriptSpeakers.map((speaker) => (
          <DetailItem key={speaker.id}>
            <DetailLabel>{speaker.label}</DetailLabel>
            <SecondaryValue>{speaker.id}</SecondaryValue>
          </DetailItem>
        ))}
      </DetailGrid>
      {media.text_content && (
        <div>
          <DetailLabel>Text Content</DetailLabel>
          <TextBlock>
            {media.transcript_segments?.length
              ? media.transcript_segments.map((segment, index) => (
                  <React.Fragment key={`${segment.speaker_id ?? 'unknown'}-${index}`}>
                    {segment.speaker_label && <SpeakerLabel>{segment.speaker_label}: </SpeakerLabel>}
                    {segment.text}
                    {index < media.transcript_segments!.length - 1 && '\n'}
                  </React.Fragment>
                ))
              : media.text_content}
          </TextBlock>
        </div>
      )}
      {media.error && (
        <div>
          <DetailLabel>Error</DetailLabel>
          <SecondaryValue>{media.error}</SecondaryValue>
        </div>
      )}
    </MediaItem>
  );
};

const RingCentralRecordMediaDetails = ({ recordId }: { recordId: number }) => {
  const { showMessage } = useRootContext();
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery<RingCentralCommunicationType>({
    queryKey: ['ringcentral-communication', recordId],
    queryFn: () => getRingCentralCommunication(recordId),
    refetchOnWindowFocus: false,
  });
  const handleActionSuccess = async (message: string) => {
    await refetch();
    queryClient.invalidateQueries(['ringcentral-communications']);
    showMessage('success', message);
  };
  const { mutate: match, isLoading: isMatching } = useMutation(() => matchRingCentralCommunication(recordId), {
    onSuccess: () => handleActionSuccess('RingCentral record matched.'),
    onError: () => showMessage('error', 'Unable to match RingCentral record.'),
  });
  const { mutate: unassign, isLoading: isUnassigning } = useMutation(() => unassignRingCentralCommunication(recordId), {
    onSuccess: () => handleActionSuccess('RingCentral record unassigned.'),
    onError: () => showMessage('error', 'Unable to unassign RingCentral record.'),
  });

  if (isLoading) {
    return (
      <LoadingContainer>
        <Spin />
      </LoadingContainer>
    );
  }

  if (isError || !data) {
    return <DetailContainer>Unable to load RingCentral record details.</DetailContainer>;
  }

  const messageContent = data.body_preview || data.subject;

  return (
    <DetailContainer>
      <ModalRecordSummary>
        <ModalSummaryItem>
          <DetailLabel>Record ID</DetailLabel>
          <PrimaryValue>{data.external_id}</PrimaryValue>
        </ModalSummaryItem>
        <ModalSummaryItem>
          <DetailLabel>Type</DetailLabel>
          <RingCentralRecordTag value={data.source_type} />
        </ModalSummaryItem>
        <ModalSummaryItem>
          <DetailLabel>Direction</DetailLabel>
          <RingCentralRecordTag value={data.direction} />
        </ModalSummaryItem>
        <ModalSummaryItem>
          <DetailLabel>From</DetailLabel>
          <ContactValue name={data.from_name} phoneNumber={data.from_phone_number} />
        </ModalSummaryItem>
        <ModalSummaryItem>
          <DetailLabel>To</DetailLabel>
          <ContactValue name={data.to_name} phoneNumber={data.to_phone_number} />
        </ModalSummaryItem>
        <ModalSummaryItem>
          <DetailLabel>Status / Date</DetailLabel>
          <ValueContainer>
            <PrimaryValue>{data.result || data.provider_status || formatLabel(data.matching_status)}</PrimaryValue>
            <SecondaryValue>{formatDate(data.occurred_at) || 'Unknown'}</SecondaryValue>
          </ValueContainer>
        </ModalSummaryItem>
        <ModalSummaryItem>
          <DetailLabel>Media</DetailLabel>
          <BadgeGroup>
            {data.recording_count > 0 && <Badge>{data.recording_count} recordings</Badge>}
            {data.transcript_count > 0 && <Badge>{data.transcript_count} transcripts</Badge>}
            {formatDuration(data.duration_seconds) && <Badge>{formatDuration(data.duration_seconds)}</Badge>}
          </BadgeGroup>
        </ModalSummaryItem>
        <ModalSummaryItem>
          <DetailLabel>Shopper</DetailLabel>
          <SecondaryValue>{data.matched_shop_request_label || 'Unassigned'}</SecondaryValue>
        </ModalSummaryItem>
        <ModalSummaryItem>
          <DetailLabel>Match</DetailLabel>
          <RingCentralRecordTag value={data.matching_status} />
        </ModalSummaryItem>
        <ActionSummaryItem>
          <Dropdown
            placement="bottomRight"
            menu={{
              items: [
                {
                  label: 'Match',
                  key: 'match',
                  disabled: data.matching_status !== 'unmatched' || isMatching || isUnassigning,
                  onClick: () => match(),
                },
                {
                  label: 'Unassign',
                  key: 'unassign',
                  disabled: data.matching_status !== 'matched' || isMatching || isUnassigning,
                  onClick: () => unassign(),
                },
              ],
            }}
          >
            <ActionsIconButton
              aria-label="Record actions"
              disabled={isMatching || isUnassigning}
              title="Actions"
              type="button"
            >
              {isMatching || isUnassigning ? <LoadingOutlined /> : <MoreOutlined />}
            </ActionsIconButton>
          </Dropdown>
        </ActionSummaryItem>
      </ModalRecordSummary>
      <GradientDivider />
      {messageContent && (
        <div>
          <DetailLabel>Message Content</DetailLabel>
          <TextBlock>{messageContent}</TextBlock>
        </div>
      )}
      {data.media.length ? (
        <MediaList className="mini-scroll">
          {getMediaGroups(data.media).map((mediaGroup) => (
            <LinkedMediaGroup $isLinked={mediaGroup.length > 1} key={mediaGroup.map((media) => media.id).join('-')}>
              {mediaGroup.map((media) => (
                <RingCentralRecordMediaItem key={media.id} media={media} />
              ))}
            </LinkedMediaGroup>
          ))}
        </MediaList>
      ) : (
        !messageContent && <EmptyValue>No message content or related media was stored for this record.</EmptyValue>
      )}
    </DetailContainer>
  );
};

const RingCentralRecordMediaModal = ({ recordId, onClose }: { recordId: number | null; onClose: () => void }) => {
  return (
    <RecordMediaModal
      centered
      className="flat"
      destroyOnClose
      footer={<Button onClick={onClose}>Close</Button>}
      onCancel={onClose}
      open={Boolean(recordId)}
      title="Record Details"
      width={1280}
    >
      {recordId && <RingCentralRecordMediaDetails recordId={recordId} />}
    </RecordMediaModal>
  );
};

export default RingCentralRecordMediaModal;
